/**
 * window.breely, as it exists inside a custom question's frame.
 *
 * This is injected ahead of the question's own code, so by the time that runs
 *  the bridge is already there. It is kept free of template literals and
 *  modern-only syntax so it can be embedded in a template literal without
 *  escaping games, and runs in whatever a client's browser happens to be.
 *
 * The frame has an opaque origin, which means every message it receives
 *  arrives with event.origin === 'null'. There is no origin to check, so the
 *  host checks the message source and both sides check a token that is
 *  generated per mount. That is also why nothing secret is ever sent in:
 *  posting to an opaque origin means posting with targetOrigin '*'.
 */
export const BRIDGE_SCRIPT = `
(function () {
    var TOKEN = window.__BREELY_TOKEN__;
    var state = {
        mode: window.__BREELY_MODE__,
        settings: window.__BREELY_SETTINGS__ || {},
        theme: window.__BREELY_THEME__ || {},
        value: window.__BREELY_VALUE__,
        answeredAt: window.__BREELY_ANSWERED_AT__ || null,
        confirmation: window.__BREELY_CONFIRMATION__ || null,
        readOnly: window.__BREELY_MODE__ !== 'fill'
    };

    var valueListeners = [];
    var confirmationListeners = [];
    var clientListeners = [];
    var hasReported = false;

    // Requests waiting on the host, by id. The host answers each one with a
    //  'result' naming the same id
    var pending = {};
    var nextRequestId = 1;

    function post(type, payload) {
        try {
            parent.postMessage({ __breely: TOKEN, type: type, payload: payload }, '*');
        } catch (e) {
            // The host went away, ex. the question was deleted while open
        }
    }

    function clone(value) {
        if (value === undefined || value === null) {
            return value;
        }

        try {
            return JSON.parse(JSON.stringify(value));
        } catch (e) {
            return null;
        }
    }

    var breely = {
        version: 1,
        mode: state.mode,
        readOnly: state.readOnly,
        settings: state.settings,
        theme: state.theme,
        answeredAt: state.answeredAt,
        confirmation: state.confirmation,

        value: state.value,

        setValue: function (value) {
            if (state.readOnly) {
                // Nothing outside fill mode is allowed to change the answer,
                //  so a question that doesn't check its mode can't rewrite a
                //  submission an admin is only reading
                return;
            }

            state.value = value;
            breely.value = value;
            post('value', clone(value));
        },

        onValueChange: function (fn) {
            if (typeof fn === 'function') {
                valueListeners.push(fn);
            }
        },

        setValid: function (isValid, message) {
            post('valid', {
                isValid: isValid !== false,
                message: typeof message === 'string' ? message : ''
            });
        },

        setCharges: function (charges) {
            post('charges', clone(charges) || []);
        },

        setHeight: function (height) {
            var px = parseInt(height, 10);

            if (!isNaN(px)) {
                post('height', px);
            }
        },

        onConfirmationChange: function (fn) {
            if (typeof fn === 'function') {
                confirmationListeners.push(fn);
            }
        },

        cancelEvent: function (eventId) {
            return request('cancelEvent', { eventId: eventId }, function () {
                // The result brings the confirmation with it, already applied
                return findEvent(eventId);
            });
        },

        getClientHistory: function () {
            return request('getClientHistory', {}, function (history) {
                return history || null;
            });
        },

        onClientChange: function (fn) {
            if (typeof fn === 'function') {
                clientListeners.push(fn);
            }
        },

        ready: function () {
            if (hasReported) {
                return;
            }

            hasReported = true;
            post('ready', { height: measure() });
        },

        error: function (message) {
            post('error', String(message === undefined ? 'Something went wrong' : message));
        }
    };

    function measure() {
        var body = document.body;
        var html = document.documentElement;

        return Math.max(
            body ? body.scrollHeight : 0,
            body ? body.offsetHeight : 0,
            html ? html.scrollHeight : 0,
            html ? html.offsetHeight : 0
        );
    }

    var lastHeight = 0;

    function reportHeight() {
        var height = measure();

        if (Math.abs(height - lastHeight) > 1) {
            lastHeight = height;
            post('height', height);
        }
    }

    function setConfirmation(confirmation) {
        // The host sends the same confirmation more than once, ex. with a
        //  result and again once the page has caught up, so only a change is
        //  passed on
        if (JSON.stringify(confirmation || null) === JSON.stringify(state.confirmation || null)) {
            return;
        }

        state.confirmation = confirmation || null;
        breely.confirmation = state.confirmation;

        for (var i = 0; i < confirmationListeners.length; i++) {
            try {
                confirmationListeners[i](state.confirmation);
            } catch (e) {
                breely.error(e && e.message ? e.message : 'A confirmation listener failed');
            }
        }
    }

    // Ask the host to do something, ex. cancel an event. Resolves with what
    //  toResult makes of the data the host answers with
    function request(type, payload, toResult) {
        return new Promise(function (resolve, reject) {
            var requestId = nextRequestId++;
            var message = { requestId: requestId };

            for (var key in payload) {
                if (Object.prototype.hasOwnProperty.call(payload, key)) {
                    message[key] = payload[key];
                }
            }

            pending[requestId] = { resolve: resolve, reject: reject, toResult: toResult };
            post(type, message);
        });
    }

    function findEvent(eventId) {
        var events = (state.confirmation && state.confirmation.events) || [];

        for (var i = 0; i < events.length; i++) {
            if (String(events[i].id) === String(eventId)) {
                return events[i];
            }
        }

        return null;
    }

    window.breely = breely;

    window.addEventListener('message', function (event) {
        if (event.source !== parent || !event.data || event.data.__breely !== TOKEN) {
            return;
        }

        var data = event.data;

        if (data.type === 'value') {
            state.value = data.payload;
            breely.value = data.payload;

            for (var i = 0; i < valueListeners.length; i++) {
                try {
                    valueListeners[i](data.payload);
                } catch (e) {
                    breely.error(e && e.message ? e.message : 'A value listener failed');
                }
            }
        }

        if (data.type === 'confirmation') {
            setConfirmation(data.payload);
        }

        if (data.type === 'result' && data.payload && pending[data.payload.requestId]) {
            var request = pending[data.payload.requestId];
            delete pending[data.payload.requestId];

            if (data.payload.confirmation) {
                setConfirmation(data.payload.confirmation);
            }

            if (data.payload.ok) {
                request.resolve(request.toResult(data.payload.data));
            } else {
                request.reject(new Error(data.payload.error || 'Something went wrong'));
            }
        }

        if (data.type === 'clientChanged') {
            for (var j = 0; j < clientListeners.length; j++) {
                try {
                    clientListeners[j]();
                } catch (e) {
                    breely.error(e && e.message ? e.message : 'A client listener failed');
                }
            }
        }

        if (data.type === 'settings') {
            state.settings = data.payload || {};
            breely.settings = state.settings;
        }
    });

    // Height follows the content. A question can still set it itself, ex. for
    //  something absolutely positioned that measures as nothing
    if (typeof ResizeObserver !== 'undefined') {
        try {
            new ResizeObserver(reportHeight).observe(document.documentElement);
        } catch (e) {
            setInterval(reportHeight, 500);
        }
    } else {
        setInterval(reportHeight, 500);
    }

    window.addEventListener('load', reportHeight);

    window.addEventListener('error', function (event) {
        breely.error(event && event.message ? event.message : 'Script error');
    });

    window.addEventListener('unhandledrejection', function (event) {
        var reason = event && event.reason;
        breely.error(reason && reason.message ? reason.message : 'A promise was rejected');
    });
})();
`;
